import React, { useEffect, useState, useRef } from 'react';
import clsx from 'clsx';
import { Link as ScrollLink, animateScroll as scroll } from "react-scroll";
import { Helmet } from "react-helmet";
import { Link, withPrefix } from "gatsby";
import Lightbox from "react-image-lightbox";
import ReactJWPlayer from "react-jw-player";
import favicon from '../../images/favicon.ico';
import eieuLogo from '../../images/eieu-logo.png';
import lttcLogo from '../../images/lttc-logo.png';
import spfeoLogo from '../../images/spfeo-logo.png';
import eieuSLogo from '../../images/eieu-s-logo.png';
import lttcSLogo from '../../images/lttc-s-logo.png';
import spfeoSLogo from '../../images/spfeo-s-logo.png';
import eduhkBwLogo from '../../images/eduhk-logo-60.png';
import libBwLogo from '../../images/eduhklib-logo-60.png';
import eieuBwLogo from '../../images/eieu-logo-60.png';
import lttcBwLogo from '../../images/lttc-bw-logo.png';
import spfeoBwLogo from '../../images/spfeo-bw-logo.png';
import aboutImage from '../../images/about.png';
import appStudents from '../../images/app-students.png';
import appSchools from '../../images/app-schools.png';
import panelImage from '../../images/panel.png';
import awardsImage from '../../images/awards.png';

export default function Home() {
  const aboutRef = useRef(null);
  const [showRegulations, setShowRegulations] = useState(false);
  const [scrollVisible, setScrollVisible] = useState(false);
  const totalSnapshots = 76;
  const [snapshotIndex, setSnapshotIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    function check() {
      if (aboutRef && aboutRef.current) {
        setScrollVisible(window.pageYOffset > aboutRef.current.offsetTop);
      }
    }
    window.addEventListener("scroll", check);
    return () => {
      window.removeEventListener("scroll", check);
    };
  }, []);

  const snapshotClicked = (index) => {
    setSnapshotIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div>
      <Helmet>
        <title>「夢想成真」產品設計比賽</title>
        <link rel="icon" href={favicon} />
      </Helmet>
      <header>
        <div className="container mx-auto px-2 py-2 lg:px-0 lg:py-0">
          <div className="text-right pt-2 lg:block hidden">
            <Link to="/">English</Link>
          </div>
          <div className="flex justify-between items-center lg:pb-4 my-1">
            <div className="lg:flex lg:items-center hidden">
              <div className="mr-4">
                <img src={eieuLogo} width="120" height="60" alt="EIEU" />
              </div>
              <div className="mr-4">
                <img src={lttcLogo} width="160" height="60" alt="LTTC" />
              </div>
              <div>
                <img src={spfeoLogo} width="160" height="60" alt="SPFEO" />
              </div>
            </div>
            <div className="lg:hidden items-center flex">
              <div className="mr-4">
                <img src={eieuSLogo} width="60" height="30" alt="EIEU" />
              </div>
              <div className="mr-4">
                <img src={lttcSLogo} width="30" height="30" alt="LTTC" />
              </div>
              <div>
                <img src={spfeoSLogo} width="30" height="30" alt="SPFEO" />
              </div>
            </div>
            <div className="lg:hidden">
              <Link to="/">English</Link>
            </div>
            <div className="lg:flex hidden">
              <ScrollLink
                to="about"
                smooth={true}
                duration={500}
                className="cursor-pointer hover:text-orange-600 font-semibold text-lg ml-8"
              >
                關於
              </ScrollLink>
              <ScrollLink
                to="schedule"
                smooth={true}
                duration={500}
                className="cursor-pointer hover:text-orange-600 font-semibold text-lg ml-8"
              >
                日程
              </ScrollLink>
              <ScrollLink
                to="adjudication"
                smooth={true}
                duration={500}
                className="cursor-pointer hover:text-orange-600 font-semibold text-lg ml-8"
              >
                評審
              </ScrollLink>
              <ScrollLink
                to="results"
                smooth={true}
                duration={500}
                className="cursor-pointer hover:text-orange-600 font-semibold text-lg ml-8"
              >
                比賽結果
              </ScrollLink>
            </div>
          </div>
        </div>
        <div className="lg:h-96 h-72 flex items-center bg-container">
          <div className="container mx-auto">
            <div className="flex items-center flex-col">
              <h1 className="lg:text-6xl text-4xl font-bold text-center">
                「夢想成真」
              </h1>
              <h1 className="lg:text-4xl text-2xl text-center mt-3">
                產品設計比賽
              </h1>
              <h3 className="lg:text-2xl text-lg text-center text-blue-theme mt-3">
                教大生＋中小生，動腦筋，助社群
              </h3>
              <ScrollLink
                to="results"
                smooth={true}
                duration={500}
                className="cta cursor-pointer tracking-wider mt-5"
              >
                查看比賽結果
              </ScrollLink>
            </div>
          </div>
        </div>
      </header>

      <main>
        <div ref={aboutRef} id="about" className="lg:py-20 py-10">
          <div className="container mx-auto px-4 lg:px-0">
            <div className="lg:flex">
              <div className="lg:flex-1 lg:pr-10">
                <h5 className="text-orange-600">#about</h5>
                <h3 className="lg:text-3xl text-xl font-bold">
                  「夢想成真」產品設計比賽
                </h3>
                <p className="leading-loose mt-3">
                  「夢想成真」是一個產品設計比賽，希望透過安排教大同學帶領由小學或中學同學所組成的學校團隊，根據大會指定的主題「
                  <b>改善目標群組的日常生活</b>
                  」設計實體產品或數碼方案，以培養香港學生的創新和創業精神，並激發他們運用
                  STEM 技能。這次活動將採用「TTT（Train The
                  Trainer）」方式，培訓教大同學成為學校團隊的領隊。
                  「夢想成真」產品設計比賽是由圖書館企業及創新教育組、教學科技中心和學校協作及體驗事務處聯合主辦，並獲大學教育資助委員會（教資會）資助。
                </p>
                <ScrollLink
                  to="section-regulations"
                  smooth={true}
                  duration={500}
                  className="inline-block cta mt-5 tracking-wider cursor-pointer"
                >
                  參賽規則
                </ScrollLink>
              </div>
              <div className="lg:w-1/3">
                <div className="lg:mt-0 mt-4">
                  <img src={aboutImage} width="500" height="300" alt="" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="schedule" className="lg:py-20 py-10 bg-gray-50 relative">
          <div className="lg:block hidden lg:opacity-50 absolute top-0 right-0 top-1/2 -mt-24 mr-14">
            <svg
              className="w-48 h-48 text-gray-300 transform rotate-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              ></path>
            </svg>
          </div>
          <div className="container mx-auto px-4 lg:px-0">
            <h5 className="text-orange-600">#schedule</h5>
            <h3 className="lg:text-3xl text-xl font-bold">比賽日程</h3>
            <div className="lg:mt-10 mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    2021 年 7 月 12 日
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    教大學生－活動簡報會
                  </h4>
                  <p className="text-gray-500 mt-1">
                    <a
                      href="https://edvideo.lib.eduhk.hk/archive/eieu/think-briefing-session-zoom/"
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      按此觀看
                    </a>
                    向教大同學介紹本活動細節的簡報會
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    2021 年 8 月 9 日
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    教大學生－申請截止
                  </h4>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    2021 年 8 月
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    教大學生－培訓
                  </h4>
                  <p className="text-gray-500 mt-1">
                    大會將為教大同學提供產品設計及原型製作工作坊，以及手機程式設計工作坊。
                    <Link
                      to="/training-for-students"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      按此查看
                    </Link>
                    工作坊內容（只有英文版本）
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    2021 年 8 月 23 日
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    學校－活動簡報會
                  </h4>
                  <p className="text-gray-500 mt-1">
                    <a
                      href="https://edvideo.lib.eduhk.hk/archive/eieu/think-briefing-schools"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      簡報會錄影
                    </a>
                  </p>
                  <p className="text-gray-500">
                    <a
                      href="/Briefing-for-schools-2021-08-23-rev-08-25.pptx"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      簡報會演示文稿
                    </a>
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    2021 年 9 月 6 日
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    學校－申請截止
                  </h4>
                  <p className="text-gray-500 mt-1">
                    受 SPFEO 邀請的本地中小學須於此日期或之前，透過活動網站
                    <a
                      href="https://edulib.me/think-sch"
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      提交申請
                    </a>
                    。
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    2021 年 9 月 7 日
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    教大學生領隊－簡報會
                  </h4>
                  <p className="text-gray-500 mt-1">
                    <a
                      href="https://edvideo.lib.eduhk.hk/archive/eieu/think-briefing-team-leaders"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      簡報會錄影
                    </a>
                  </p>
                  <p className="text-gray-500">
                    <a
                      href="/Think-Design-Team-Leaders-Guideline-2021-09-07.docx.pdf"
                      target="_blank"
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      簡報會演示文稿
                    </a>
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    2021 年 9 月 11 日
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">
                    學校－參觀未來教室及初創基地
                  </h4>
                  <p className="text-gray-500 mt-1">
                    有興趣參與學校將獲安排參觀各未來教室及初創基地。
                  </p>
                  <p className="text-red-500">
                    惡劣天氣安排：如3號強風信號或紅色暴雨警告於當天上午7:30或之後生效，參觀將順延一週至9月18日上午。
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    2021 年 9 月 13 日
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">指派領隊</h4>
                  <p className="text-gray-500 mt-1">
                    每位被取錄的教大同學會以隨機方式，被指派擔任一組被取錄學校團隊的領隊。
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    2021 年 11 月 26 日
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">提交作品</h4>
                  <p className="text-gray-500 mt-1">
                    每組學校團隊必須於此日期或之前向 EIEU 提交作品。
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-5">
              <div className="h-1 bg-separator mb-5"></div>
              <div className="flex mt-5">
                <div className="w-1/3">
                  <h4 className="font-bold lg:text-xl text-lg pr-4 lg:pr-0">
                    2021 年 12 月 2 日及 3 日
                  </h4>
                </div>
                <div className="flex-1">
                  <h4 className="font-bold lg:text-xl text-lg">最後演示</h4>
                  <p className="text-gray-500 mt-1">
                    學校團隊已於教大蒙民偉圖書館二樓創科教室向評審團作最後演示。
                    <ScrollLink
                      to="results"
                      smooth={true}
                      duration={500}
                      className="text-blue-600 hover:text-blue-700 underline"
                    >
                      按此查看
                    </ScrollLink>
                    最後演示精彩花絮
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div id="adjudication" className="lg:py-20 py-10">
          <div className="container mx-auto px-4 lg:px-0">
            <div className="lg:flex">
              <div className="lg:flex-1 lg:pr-10">
                <h5 className="text-orange-600">#adjudication</h5>
                <h3 className="lg:text-3xl text-xl font-bold">評審</h3>
                <p className="leading-loose mt-3">
                  評審團由3名成員組成，包括教大職員及校外專家。
                </p>
              </div>
              <div className="lg:w-1/3">
                <img src={panelImage} width="500" height="300" alt="" />
              </div>
            </div>
          </div>
        </div>

        <div id="results" className="lg:py-20 py-10 bg-gray-50">
          <div className="container mx-auto px-4 lg:px-0">
            <div className="lg:flex">
              <div className="lg:flex-1 lg:pr-10">
                <h5 className="text-orange-600">#final_results</h5>
                <h3 className="lg:text-3xl text-xl font-bold">比賽結果</h3>
                <p className="leading-loose mt-3">
                  現公布「夢想成真」產品設計比賽結果：
                </p>
                <div className="mt-4 mb-4">
                  <h5 className="font-bold text-lg">「學校團隊」組別：</h5>
                  <table className="w-full bg-orange-50 table-auto my-3">
                    <thead>
                      <tr>
                        <th
                          width="50%"
                          className="border border-black px-2 py-2 text-left"
                        >
                          獎項
                        </th>
                        <th className="border border-black px-2 py-2 text-left">
                          隊伍
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-black px-2 py-2">
                          <b>冠軍 x 1:</b>
                          <ul className="list-disc ml-10">
                            <li>教大同學領隊將獲得$2,000書券及嘉許獎狀</li>
                            <li>學校團隊將獲得$2,000書券及嘉許獎狀</li>
                          </ul>
                        </td>
                        <td className="border border-black px-2 py-2">
                          東華三院李嘉誠中學（Deaf and Dog Care Device）
                        </td>
                      </tr>
                      <tr>
                        <td className="border border-black px-2 py-2">
                          <b>優異獎 x 5:</b>
                          <ul className="list-disc ml-10">
                            <li>每位教大同學領隊將獲得$1,000書券及嘉許獎狀</li>
                            <li>每組學校團隊將獲得$1,000書券及嘉許獎狀</li>
                          </ul>
                        </td>
                        <td className="border border-black px-2 py-2">
                          <ul className="list-disc ml-10">
                            <li>
                              宣道會鄭榮之中學（JOYstick肌肉萎縮症患者專用電動輪椅操縱桿）
                            </li>
                            <li>胡素貞博士紀念學校（救命鐘）</li>
                            <li>英華書院（Airbag Stand Up Aid System）</li>
                            <li>通德學校（乖孫提你服藥杯）</li>
                            <li>潮陽百欣小學（「智惜食」智能食物裝置）</li>
                          </ul>
                          <p className="mt-2 text-sm italic">(排名不分先後)</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="mt-8 mb-4">
                  <h5 className="font-bold text-lg">「個別學校團隊」組別：</h5>
                  <table className="w-full bg-orange-50 table-auto my-3">
                    <thead>
                      <tr>
                        <th
                          width="50%"
                          className="border border-black px-2 py-2 text-left"
                        >
                          獎項
                        </th>
                        <th className="border border-black px-2 py-2 text-left">
                          隊伍
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="border border-black px-2 py-2">
                          <b>冠軍:</b>
                          <ul className="list-disc ml-10">
                            <li>團隊將獲得$2,000書券及嘉許獎狀</li>
                          </ul>
                        </td>
                        <td className="border border-black px-2 py-2">
                          禮賢會彭學高紀念中學（智能杯墊）
                        </td>
                      </tr>
                      <tr>
                        <td className="border border-black px-2 py-2">
                          <b>特別獎 x 2:</b>
                          <ul className="list-disc ml-10">
                            <li>每組團隊將獲得$1,500書券及嘉許獎狀</li>
                          </ul>
                        </td>
                        <td className="border border-black px-2 py-2">
                          <ul className="list-disc ml-10">
                            <li>
                              中華基督教會協和小學（Oh! “Eye” See Mobile App）
                            </li>
                            <li>
                              中華基督教會銘基書院（Skin Diseaseless Mobile
                              App）
                            </li>
                          </ul>
                          <p className="mt-2 text-sm italic">(排名不分先後)</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="mt-8 mb-4">
                  <h5 className="font-bold text-lg" id="video">
                    最後演示精彩花絮：
                  </h5>

                  <ReactJWPlayer
                    playerId="player"
                    playerScript="https://cdn.jwplayer.com/libraries/l3BxhrLE.js"
                    file="https://stream.lib.eduhk.hk:1935/info/mp4:eieu-think-bts.mp4/playlist.m3u8"
                    image={withPrefix("/bts-snap.jpg")}
                    isAutoPlay={false}
                    className="w-3/4 mx-auto mt-5"
                  />
                </div>

                <div className="mt-8 mb-4">
                  <h5 className="font-bold text-lg">相片：</h5>
                  <div className="mt-5 grid grid-cols-2 lg:grid-cols-8 gap-4">
                    {[...Array(totalSnapshots)].map((item, i) => (
                      <img
                        key={i}
                        src={withPrefix(
                          `/snapshots/thumbnails/pic${i + 1}.jpg`
                        )}
                        className="h-32 object-cover cursor-pointer inline-block"
                        alt=""
                        onClick={() => snapshotClicked(i)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {lightboxOpen && (
        <Lightbox
          mainSrc={withPrefix(
            `/snapshots/originals/pic${snapshotIndex + 1}.jpg`
          )}
          nextSrc={
            snapshotIndex >= totalSnapshots - 1
              ? null
              : withPrefix(`/snapshots/originals/pic${snapshotIndex + 2}.jpg`)
          }
          prevSrc={
            snapshotIndex === 0
              ? null
              : withPrefix(`/snapshots/originals/pic${snapshotIndex - 1}.jpg`)
          }
          onCloseRequest={() => setLightboxOpen(false)}
          onMovePrevRequest={() => setSnapshotIndex(snapshotIndex - 1)}
          onMoveNextRequest={() => setSnapshotIndex(snapshotIndex + 1)}
        />
      )}

      <footer className="bg-black py-10 px-10 md:py-20 md:px-20">
        <div className="container mx-auto">
          <div className="flex items-center justify-center">
            <div className="text-white">
              <p className="text-orange-400 mb-3 font-semibold text-center">
                聯絡我們
              </p>
              <p className="text-center">香港教育大學圖書館企業及創新教育組</p>
              <p className="text-center">電話: 29486511</p>
              <p className="text-center">
                電郵:{" "}
                <a href="mailto:eieu@eduhk.hk" className="underline">
                  eieu@eduhk.hk
                </a>
              </p>
              <ul className="mt-6 list-style-none flex items-center justify-center">
                <li className="mr-6">
                  <a
                    href="https://www.eduhk.hk"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="opacity-50 hover:opacity-100"
                      src={eduhkBwLogo}
                      width="30"
                      height="20"
                      alt="EdUHK"
                    />
                  </a>
                </li>
                <li className="mr-6">
                  <a
                    href="https://www.lib.eduhk.hk"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="opacity-50 hover:opacity-100"
                      src={libBwLogo}
                      width="30"
                      height="20"
                      alt="EdUHK Library"
                    />
                  </a>
                </li>
                <li className="mr-6">
                  <a
                    href="https://eieu.lib.eduhk.hk"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="opacity-50 hover:opacity-100"
                      src={eieuBwLogo}
                      width="40"
                      height="20"
                      alt="EIEU"
                    />
                  </a>
                </li>
                <li className="mr-4">
                  <a
                    href="https://www.lttc.eduhk.hk"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="opacity-50 hover:opacity-100"
                      src={lttcBwLogo}
                      width="30"
                      height="20"
                      alt="LTTC"
                    />
                  </a>
                </li>
                <li>
                  <a
                    href="https://www.eduhk.hk/spfeo"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="opacity-50 hover:opacity-100"
                      src={spfeoBwLogo}
                      width="30"
                      height="20"
                      alt="SPFEO"
                    />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </footer>

      <div
        className={clsx(
          "fixed",
          "bottom-0",
          "right-0",
          "mr-5",
          "mb-10",
          "md:mr-20",
          "md:mb-20",
          !scrollVisible && "hidden"
        )}
      >
        <button
          onClick={() => scroll.scrollToTop({ duration: 500 })}
          className="w-10 h-10 md:w-12 md:h-12 bg-orange-500 shadow-lg rounded-full inline-block flex items-center justify-center focus:outline-none text-white"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            ></path>
          </svg>
        </button>
      </div>
    </div>
  );
}